import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ChefHat, Plus, Trash2, Edit, ClipboardList } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;

const categorias = ["platillo", "bebida", "snack", "postre", "salsa", "base"];

const emptyReceta = {
  nombre: "", categoria: "platillo", porciones_base: 1,
  ingredientes: [], tiempo_prep_min: 0, instrucciones: "", notas: "",
};
const emptyIng = { producto: "", cantidad: 0, unidad: "kg", costo_unitario: 0, merma_pct: 0 };

const calcCostoReceta = (r) => {
  let costo = 0;
  for (const i of r.ingredientes || []) {
    const qty = Number(i.cantidad) || 0;
    const cost = Number(i.costo_unitario) || 0;
    const merma = Number(i.merma_pct) || 0;
    costo += qty * cost * (1 + merma / 100);
  }
  return costo;
};

const Recetas = () => {
  const [recetas, setRecetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyReceta);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/recetas`);
      setRecetas(r.data || []);
    } catch { toast.error("Error al cargar recetas"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setEditing(null); setForm(emptyReceta); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...emptyReceta, ...r }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.nombre?.trim()) { toast.error("Nombre requerido"); return; }
    try {
      const payload = {
        ...form,
        porciones_base: Math.max(Number(form.porciones_base) || 1, 1),
        tiempo_prep_min: Number(form.tiempo_prep_min) || 0,
        ingredientes: (form.ingredientes || []).map((i) => ({
          ...i,
          cantidad: Number(i.cantidad) || 0,
          costo_unitario: Number(i.costo_unitario) || 0,
          merma_pct: Number(i.merma_pct) || 0,
        })),
      };
      if (editing) {
        await axios.put(`${API}/recetas/${editing.receta_id}`, payload);
        toast.success("Receta actualizada");
      } else {
        await axios.post(`${API}/recetas`, payload);
        toast.success("Receta creada");
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar receta?")) return;
    try {
      await axios.delete(`${API}/recetas/${id}`);
      toast.success("Eliminada");
      fetchData();
    } catch { toast.error("Error"); }
  };

  const addIng = () => setForm({ ...form, ingredientes: [...(form.ingredientes || []), { ...emptyIng }] });
  const updIng = (idx, field, val) => {
    const ings = [...(form.ingredientes || [])];
    ings[idx] = { ...ings[idx], [field]: val };
    setForm({ ...form, ingredientes: ings });
  };
  const delIng = (idx) => setForm({ ...form, ingredientes: form.ingredientes.filter((_, i) => i !== idx) });

  const costoActual = calcCostoReceta(form);
  const porciones = Math.max(Number(form.porciones_base) || 1, 1);
  const costoPorPorcion = costoActual / porciones;

  return (
    <div className="space-y-6" data-testid="recetas-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-stone-900">Fichas tecnicas / Recetas</h1>
          <p className="text-stone-500 text-sm mt-1">Costeo real por porcion con ingredientes y merma.</p>
        </div>
        <Button onClick={openNew} className="bg-emerald-900 hover:bg-emerald-800" data-testid="btn-nueva-receta">
          <Plus className="w-4 h-4 mr-2" /> Nueva receta
        </Button>
      </div>

      {loading ? (
        <div className="text-stone-500 text-sm">Cargando...</div>
      ) : recetas.length === 0 ? (
        <Card className="border-stone-200">
          <CardContent className="p-8 text-center text-stone-500">
            <ChefHat className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p className="text-sm">Sin recetas registradas.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {recetas.map((r) => {
            const costo = calcCostoReceta(r);
            const porc = Math.max(Number(r.porciones_base) || 1, 1);
            return (
              <Card key={r.receta_id} className="border-stone-200" data-testid={`card-receta-${r.receta_id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-display text-lg text-stone-900">{r.nombre}</h3>
                      <p className="text-xs text-stone-500 capitalize">{r.categoria} · {r.porciones_base} porciones</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`btn-edit-receta-${r.receta_id}`}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(r.receta_id)} data-testid={`btn-del-receta-${r.receta_id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                    </div>
                  </div>
                  <div className="text-xs text-stone-600">
                    <ClipboardList className="w-3 h-3 inline mr-1" /> {(r.ingredientes || []).length} ingredientes
                  </div>
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-stone-100">
                    <div>
                      <p className="text-xs text-stone-500">Costo total</p>
                      <p className="text-sm font-semibold text-stone-900">{fmt(costo)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-stone-500">Por porcion</p>
                      <p className="text-sm font-semibold text-emerald-800">{fmt(costo / porc)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-receta">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar receta" : "Nueva receta"}</DialogTitle>
            <DialogDescription className="text-xs">Define ingredientes y % de merma para calcular costo real por porcion.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label>Nombre *</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="receta-nombre" />
              </div>
              <div>
                <Label>Categoria</Label>
                <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{categorias.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Porciones base</Label>
                <Input type="number" min="1" value={form.porciones_base} onChange={(e) => setForm({ ...form, porciones_base: e.target.value })} data-testid="receta-porciones" />
              </div>
              <div>
                <Label>Tiempo prep. (min)</Label>
                <Input type="number" value={form.tiempo_prep_min} onChange={(e) => setForm({ ...form, tiempo_prep_min: e.target.value })} />
              </div>
            </div>

            <div className="border border-stone-200 rounded-lg p-3 bg-stone-50">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold">Ingredientes</Label>
                <Button size="sm" variant="outline" onClick={addIng} data-testid="btn-add-ingrediente">
                  <Plus className="w-3 h-3 mr-1" /> Agregar
                </Button>
              </div>
              {(form.ingredientes || []).length === 0 ? (
                <p className="text-stone-500 text-xs">Sin ingredientes.</p>
              ) : (
                <div className="space-y-2">
                  {form.ingredientes.map((ing, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white p-2 rounded border border-stone-200">
                      <div className="col-span-12 sm:col-span-4">
                        <Label className="text-xs">Producto</Label>
                        <Input value={ing.producto} onChange={(e) => updIng(idx, "producto", e.target.value)} data-testid={`ing-producto-${idx}`} />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Label className="text-xs">Cant.</Label>
                        <Input type="number" step="0.01" value={ing.cantidad} onChange={(e) => updIng(idx, "cantidad", e.target.value)} data-testid={`ing-cantidad-${idx}`} />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Label className="text-xs">Unidad</Label>
                        <Input value={ing.unidad} onChange={(e) => updIng(idx, "unidad", e.target.value)} />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Label className="text-xs">$/unidad</Label>
                        <Input type="number" step="0.01" value={ing.costo_unitario} onChange={(e) => updIng(idx, "costo_unitario", e.target.value)} data-testid={`ing-costo-${idx}`} />
                      </div>
                      <div className="col-span-8 sm:col-span-1">
                        <Label className="text-xs">Merma%</Label>
                        <Input type="number" step="0.1" value={ing.merma_pct} onChange={(e) => updIng(idx, "merma_pct", e.target.value)} data-testid={`ing-merma-${idx}`} />
                      </div>
                      <div className="col-span-4 sm:col-span-1 text-right">
                        <Button size="sm" variant="ghost" onClick={() => delIng(idx)} data-testid={`btn-del-ing-${idx}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div>
                <p className="text-xs text-stone-600">Costo total estimado</p>
                <p className="text-lg font-semibold text-stone-900" data-testid="receta-costo-total">{fmt(costoActual)}</p>
              </div>
              <div>
                <p className="text-xs text-stone-600">Costo por porcion</p>
                <p className="text-lg font-semibold text-emerald-800" data-testid="receta-costo-porcion">{fmt(costoPorPorcion)}</p>
              </div>
            </div>

            <div>
              <Label>Instrucciones</Label>
              <Textarea rows={3} value={form.instrucciones || ""} onChange={(e) => setForm({ ...form, instrucciones: e.target.value })} />
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea rows={2} value={form.notas || ""} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} className="bg-emerald-900 hover:bg-emerald-800" data-testid="btn-guardar-receta">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Recetas;
